<template>
  <Toast ref="toast" />
  <Card
    class="lot-card"
    v-tooltip.bottom="{
      // value: tooltipContent,
      escape: false,
      pt: {
        root: {
          class: 'custom-tooltip'
        }
      }
    }"
  >
    <template #content>
      <div class="lot" @click="copyToClipboard(item.name)">
        <strong
          :style="{
            color:
              item.id === 'dm6l2_1'
                ? '#4CAF50'
                : item.id === 'dm6l2_2'
                ? '#2196F3'
                : item.id === 'dm6l2_3'
                ? '#9C27B0'
                : item.id === 'dm6l2_4'
                ? '#F44336'
                : '#E0E0E0'
          }"
          >{{ item.name }}</strong
        >
        ({{ formatPrice(item.historyAveragePrice) }})
      </div>
      <div style="display: flex; flex-wrap: wrap">
        <div v-for="itemSize in item.minimal" :key="`${itemSize.size}-${itemSize.value}`">
          <Tag
            :severity="getPriceSeverity(itemSize)"
            class="lot-size"
            @click="copyToClipboardPrice(itemSize.value)"
          >
            {{ itemSize.size }}: {{ formatPrice(itemSize.value) }}
          </Tag>
        </div>
      </div>
    </template>
  </Card>

  <Card v-if="filteredProfitList.length" class="auction-card">
    <template #content>
      <DataTable
        :value="filteredProfitList"
        responsiveLayout="scroll"
        :sortField="'priceDifferenceResult'"
        :sortOrder="-1"
      >
        <Column field="buyoutPrice" header="Общая цена" :sortable="true" :sortField="'buyoutPrice'">
          <template #body="{ data }">
            <span :style="styleColor(data)">
              {{ formatPrice(data.buyoutPrice) }} ({{ data.amount }})
            </span>
            <span style="color: #10b981" v-if="data.startPriceOne">1Р</span>

            <span style="color: yellow" v-if="data.duration48">48Ч</span>
          </template>
        </Column>
        <Column
          field="averagePrice"
          header="Средняя цена"
          :sortable="true"
          :sortField="'averagePrice'"
        >
          <template #body="{ data }">
            {{ formatPrice(data.averagePrice) }}
          </template>
        </Column>
        <Column
          field="priceDifferenceResult"
          header="Прибыль"
          :sortable="true"
          :sortField="'priceDifferenceResult'"
        >
          <template #body="{ data }">
            <Tag :severity="data.priceDifferenceResult > 0 ? 'success' : 'danger'">
              {{ formatPrice(data.priceDifferenceResult) }} ₽
            </Tag>
          </template>
        </Column>
      </DataTable>
    </template>
  </Card>
</template>

<script setup lang="ts">
import { ref, computed, defineProps } from 'vue'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Card from 'primevue/card'
import Tag from 'primevue/tag'
import Toast from 'primevue/toast'
import { copyText } from 'vue3-clipboard'
import { formatPrice } from '@/common/function'
import type { MinimalPrice, TableItem } from '@/types/auction'

const toast = ref()

const props = defineProps<{
  item: TableItem
  enteredProfitLots: number
}>()

function styleColor(data) {
  if (data.startPriceOne && data.duration48) {
    return 'color: #d68686'
  } else if (data.startPriceOne) {
    return 'color: #10b981'
  } else if (data.duration48) {
    return 'color: yellow'
  } else {
    return ''
  }
}

const filteredProfitList = computed(() => {
  return props.item.profitList
    .filter(item => item.priceDifferenceResult > props.enteredProfitLots)
    .map(item => ({
      ...item,
      buyoutPrice: Number(item.buyoutPrice) || 0,
      averagePrice: Number(item.averagePrice) || 0,
      priceDifferenceResult: Number(item.priceDifferenceResult) || 0
    }))
})

const getPriceSeverity = (itemSize: MinimalPrice) => {
  if (!itemSize.value) return 'info'
  return props.item.historyAveragePrice * itemSize.size > itemSize.value ? 'danger' : 'success'
}

const copyingNotification = (text: string) => {
  toast.value?.add({
    severity: 'info',
    summary: 'Скопировано',
    detail: text,
    life: 3000
  })
}

const copyToClipboard = (text: string) => {
  copyingNotification(text)
  copyText(text, undefined, (error: Error) => {
    if (error) console.error('Copy error:', error)
  })
}

const copyToClipboardPrice = (price: number | null) => {
  if (price === null) return
  copyingNotification(price.toString())
  copyText(price - 1, undefined, (error: Error) => {
    if (error) console.error('Copy error:', error)
  })
}

const tooltipContent = computed(() => {
  return `
  <div class="price-history-tooltip">
    <div class="tooltip-title">История цен</div>
    <table class="tooltip-table">
      <thead>
        <tr>
          <th>Период</th>
          <th>Средняя цена</th>
          <th>Продано</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>1 час</td>
          <td>${props.item.stats.hourly.average} ₽</td>
          <td>${props.item.stats.hourly.totalAmount}</td>
        </tr>
        <tr>
          <td>1 день</td>
          <td>${props.item.stats.daily.average} ₽</td>
          <td>${props.item.stats.daily.totalAmount}</td>
        </tr>
        <tr>
          <td>3 дня</td>
          <td>${props.item.stats.threeDays.average} ₽</td>
          <td>${props.item.stats.threeDays.totalAmount}</td>
        </tr>
        <tr>
          <td>1 неделя</td>
          <td>${props.item.stats.weekly.average} ₽</td>
          <td>${props.item.stats.weekly.totalAmount}</td>
        </tr>
      </tbody>
    </table>
  </div>
`
})
</script>

<style lang="scss">
.lot-card,
.auction-card {
  margin: 6px auto;
}

.lot {
  cursor: pointer;
  font-size: 1.2em;
  margin-bottom: 10px;
}

.lot-size {
  cursor: pointer;
  display: inline-block;
  margin: 5px;
}

.custom-tooltip {
  background: #2d3748 !important;
  border: 1px solid #4a5568 !important;
  border-radius: 6px !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3) !important;
  padding: 12px !important;
  min-width: 260px !important;
  max-width: 90vw !important;
  white-space: nowrap !important;

  &:after {
    border-color: #2d3748 transparent transparent transparent !important;
    filter: drop-shadow(0 -2px 1px rgba(0, 0, 0, 0.2));
  }
}

.price-history-tooltip {
  font-family: 'Segoe UI', system-ui, sans-serif;
  line-height: 1.4;
  margin-top: -20px;
  margin-bottom: -20px;

  .tooltip-title {
    color: #e2e8f0 !important;
    font-size: 14px;
    font-weight: 600;
    padding-bottom: 8px;
    border-bottom: 1px solid #4a5568 !important;
  }

  .tooltip-table {
    width: auto !important;
    border-collapse: collapse;

    th {
      color: #a0aec0 !important;
      font-weight: 500;
      font-size: 12px;
      text-align: left;
      padding: 4px 16px 4px 0;
      white-space: nowrap !important;
    }

    td {
      color: #edf2f7 !important;
      font-size: 13px;
      padding: 6px 16px 6px 0;
      white-space: nowrap !important;
      text-align: center !important;

      &:first-child {
        color: #cbd5e0 !important;
        padding-right: 24px !important;
      }

      &:nth-child(1) {
        text-align: left !important;
      }
    }

    tr:not(:last-child) td {
      border-bottom: 1px solid #4a5568 !important;
    }
  }
}
</style>
